import {Annotation} from "react-ace/types";

export type Round = {
    stitches: string[],
    type: "Cast On" | "Knit",
    properties: {
        inc: number,
        dec: number,
        err: boolean,
        line: number
    }
}

export type Pattern = {
    rows: Round[],
    gauge: number[]
}

export function parse(text: string): {pattern: Pattern, errors: Annotation[]} {
    const lines = text.split("\n");
    const rounds: Map<number, Round> = new Map();
    const pattern: Round[] = [];
    const errors: Annotation[] = [];
    const gauge: number[] = [];

    for (let textLineNum = 0; textLineNum < lines.length;  textLineNum++) {
        try {
            const line = lines[textLineNum];

            const regexpRound = new RegExp(/(Round\s*)([0-9]*)(:\s*)/i);
            const regexpRepeat = new RegExp(/Repeat/i);
            const regexpCastOn = new RegExp(/Cast\s*On/i);
            const regexpGauge = new RegExp(/Gauge:/i);

            if (regexpCastOn.test(line)) {
                const numCastOn = Number(line.match(/(\d+)(?=\s*stitches)/i)![0]);
                const castOnStitches = [];
                for (let i = 0; i < numCastOn; i++) {
                    castOnStitches.push("k");
                }
                pattern.push({
                    stitches: castOnStitches,
                    type: "Cast On",
                    properties: {
                        inc: 0,
                        dec: 0,
                        err: false,
                        line: textLineNum
                    }
                });
            } else if (regexpRound.test(line)) {
                const roundInfo = line.split(':', 2);
                const roundName = roundInfo[0].match(/(?:Round\s*)([0-9]*)/i)!;
                const roundNumber = Number(roundName[1]);

                const splits = roundInfo[1].split(/\(/g);
                let totalStitches: string[] = [];
                let totalDec = 0;
                for (const parsingStitches of splits) {
                    //is it a group?
                    const isRepeat = parsingStitches.match(/\)/);

                    if (!isRepeat) {
                        // parse stitches before group
                        const {stitches, dec} = parseStitchList(parsingStitches);
                        totalStitches = totalStitches.concat(stitches);
                        totalDec += dec;

                    } else {
                        // parse group
                        const parsingGroup = parsingStitches.split(/\)/, 2);
                        const repeatStitches = parsingGroup[0];
                        const repeatTimesText = parsingGroup[1];
                        let {stitches, dec} = parseStitchList(repeatStitches);

                        const repeatTimes = Number(repeatTimesText.match(/(\d+)(?=\s*times)/i)![0]);
                        for (let i = 0; i < repeatTimes; i++) {
                            totalStitches = totalStitches.concat(stitches);
                            totalDec += dec;
                        }

                        //parse stitches after group
                        const afterStitches = repeatTimesText.split(/,(.*)/);
                        if (afterStitches.length > 1) {
                            const afterSts = parseStitchList(afterStitches[1]);
                            totalStitches = totalStitches.concat(afterSts.stitches);
                            totalDec += afterSts.dec;
                        }
                    }
                }

                const properties = {
                    err: false,
                    dec: totalDec,
                    inc: 0,
                    line: textLineNum
                };

                if (pattern.length === 0) {
                    errors.push({
                        row: textLineNum,
                        column: 0,
                        text: `Error: These stitches haven't been cast on!`,
                        type: "error"
                    });

                    properties.err = true;
                } else {
                    const lastRoundLength = pattern[pattern.length - 1].stitches.length;
                    const thisRoundLength = totalStitches.length + properties.dec - properties.inc;

                    if (lastRoundLength !== thisRoundLength) {
                        errors.push({
                            row: textLineNum,
                            column: 0,
                            text: `Error: Wrong number of stitches`,
                            type: "error"
                        });

                        properties.err = true;
                    }
                }
                const r: Round = {
                    stitches: totalStitches,
                    type: "Knit",
                    properties
                };
                pattern.push(r);
                rounds.set(roundNumber, r)
            } else if (regexpRepeat.test(line)) {
                const plural = new RegExp(/and/i);

                let roundsToRepeat: number[] = [];
                if (plural.test(line)) {
                    const roundsInfo = line.split('and', 2);
                    roundsToRepeat = roundsInfo[0].match(/(\s*\d+)(?:\s*,*)/g)!.map(round => {
                        let r = round.replace(",", "");
                        r.trim();
                        return Number(r);
                    });

                    const roundAfterAnd = roundsInfo[1].match(/(\s*\d+)/)![0];
                    roundsToRepeat.push(Number(roundAfterAnd));
                } else {
                    roundsToRepeat.push(Number(line.match(/Repeat\s*Round\s*(\d+)/i)![1]));
                }

                const repeatTimes = line.match(/(\d+)(?=\s*more times)/i)![0];
                const times = Number(repeatTimes);
                let foundErrors = false;
                for (let i = times; i > 0; i--) {
                    roundsToRepeat.forEach((round: number) => {
                        if (rounds.has(round)) {
                            const roundRef = rounds.get(round)!;
                            const properties = {
                                err: false,
                                inc: roundRef.properties.inc,
                                dec: roundRef.properties.dec,
                                line: textLineNum
                            };

                            const lastRoundLength = pattern[pattern.length - 1].stitches.length;
                            const thisRoundLength = roundRef.stitches.length + properties.dec - properties.inc;
                            if (lastRoundLength !== thisRoundLength) {
                                properties.err = true;
                                foundErrors = true;
                            }

                            pattern.push({
                                stitches: roundRef.stitches,
                                type: "Knit",
                                properties
                            });
                        }
                    });
                }

                if (foundErrors) {
                    errors.push({
                        row: textLineNum,
                        column: 0,
                        text: `Error: Wrong number of stitches`,
                        type: "error"
                    });
                }
            } else if (regexpGauge.test(line)) {
                const stitchesRegExp = new RegExp(/(\d+)(?=\s*stitch)/i);
                const rowsRegExp = new RegExp(/(\d+)(?=\s*row)/i);

                const stitchesMatch = line.match(stitchesRegExp);
                const rowsMatch = line.match(rowsRegExp);

                if (stitchesMatch && rowsMatch) {
                    const stitches = Number(stitchesMatch[0]);
                    const rows = Number(rowsMatch[0]);
                    gauge.push(stitches);
                    gauge.push(rows);
                }

            }
        } catch (error: any) {
            errors.push({
                row: textLineNum,
                column: 0,
                text: `Error: Cannot understand this line. Please check spelling and for missing words.`,
                type: "error"
            });
        }
    }

    for (let i = 0; i < pattern.length; i++){
        const row = pattern[i];

        //odd rows reverse stitches
        if (i % 2 === 1) {
            const newStitches = []
            for (let j = row.stitches.length-1; j > -1; j--) {
                const stitch = row.stitches[j];
                if (stitch === "k") {
                    newStitches.push("p");
                } else if (stitch === "p") {
                    newStitches.push("k");
                }
            }
            row.stitches = newStitches;
        } else {
            const newStitches = [...row.stitches];
            newStitches.reverse();

            row.stitches = newStitches;
        }
    }

    const reversePattern = pattern.reverse();
    return {pattern: {rows: reversePattern, gauge}, errors};
}

export function parseStitchList(stitchList: string): {stitches: string[], dec: number} {
    const stitches = stitchList.match(/([kp])([0-9]*)(tog)?/g);
    if (!stitches) {
        return {
            stitches: [],
            dec: 0
        }
    }

    const stitchesLong = [];
    let numDec = 0;
    for (let stitch of stitches!) {
        const symbol = /[kp]/.exec(stitch)![0];
        const times = Number(/\B[0-9]*/.exec(stitch)![0]);
        const dec = /(tog)/.exec(stitch);

        if (dec) {
            stitchesLong.push(symbol);
            numDec += times - 1;
        } else {
            for (let i = 0; i < times; i++) {
                stitchesLong.push(symbol);
            }
        }
    }

    return {
        stitches: stitchesLong,
        dec: numDec
    }
}